import React, { useState } from 'react'

import _ from 'lodash'

import MTable from '../../../../../UI/Table/Table'

import { TextField, Grid, Typography, Chip, withStyles } from '@material-ui/core'
import Toggle from '../../../../../UI/Toggle/Toggle'
import { Add, Delete, Info } from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import DetailPanel from './DetailPanel/DetailPanel'
import Aux from '../../../../../../hoc/Auxiliary/Auxiliary'

import withTranslate from 'react-redux-multilingual/lib/withTranslate'
import { camelCase } from 'lodash'
import { isTrulyEqual, displayNumber, getPureNumber } from '../../../../../../helpers/utils'
import Collapse from '../../../../../UI/Collapse/Collapse'
import InputGroup from '../../../../../UI/Forms/InputGroup/InputGroup'
import ConfirmationModal from '../../../../../ConfirmationModal/ConfirmationModal'
import { useCallback } from 'react'
import PerformanceLevelModal from '../../../../Appraisal/AppraisalResult/StructureTabs/StructureTable/PerformanceLevelModal/PerformanceLevelModal'

function StructureTable({
  inputChanged,
  groupInputChanged,
  groupItemInputChanged,
  groupItemDeleted,
  frequencyOptions,
  structureData,
  applyButtonClicked,
  addButtonClicked,
  deleteRowClicked,
  responsibleSearchChanged,
  actionDisabled,
  assignmentTargetIds,
  fireAlert,
  translate,
  maxScore,
  appraisalType,
  isValueGetZero,
  loadingQueue
}) {
  const structureCode = structureData?.structure_type?.value_list_code || 'STT01'
  const isThresholdScore = structureData?.is_threshold && structureCode === 'STT02'
  const structureGetZero = !!structureData?.is_get_zero
  const structureScoreOptions = structureData?.competency_score || []

  const [modalOpen, setModalOpen] = useState(false)
  const [targetRow, setTargetRow] = useState(null)
  const [collapseOpen, setCollapseOpen] = useState([])
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const deleteItemClickedHandler = useCallback(
    rowData => {
      setConfirmDialogOpen(true)
      setTargetRow(rowData)
    },
    [setConfirmDialogOpen, setTargetRow]
  )

  const collapseBarClickedHandler = (groupId, event) => {
    if (event.target.localName === 'input') {
      return
    }
    setCollapseOpen(prevState => {
      if (prevState.includes(groupId)) {
        return prevState.filter(item => item !== groupId)
      } else {
        return [...prevState, groupId]
      }
    })
  }

  const tableStructures = {
    STT01: [
      {
        field: 'item_code',
        title: translate('kpiCode'),
        width: 100,
        editable: 'never'
      },
      {
        field: 'item_name',
        title: translate('kpiName'),
        editable: 'never',
        width: 350,
        render: rowData => {
          return (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant={'body2'}>{rowData.item_name}</Typography>
              <StyledChip
                style={{ marginLeft: 5 }}
                icon={<Info />}
                variant={'outlined'}
                size={'small'}
                color={'primary'}
                onClick={() => openModalHandler(rowData)}
              />
            </span>
          )
        }
      },
      {
        field: 'frequency_type_id',
        title: translate('frequency'),
        width: 150,
        render: rowData => {
          return (
            <Autocomplete
              value={
                frequencyOptions.find(item => item.value_list_id === rowData.frequency_type_id) ||
                null
              }
              onChange={(e, value) =>
                inputChanged({
                  ...rowData,
                  frequency_type_id: value?.value_list_id,
                  result_kpi: []
                })
              }
              disabled={!!rowData.is_adopt}
              options={frequencyOptions}
              getOptionLabel={option => translate(camelCase(option.value_list_name)) || ''}
              renderInput={params => (
                <TextField
                  style={{ width: '100%' }}
                  {...params}
                  disabled={!!rowData.is_adopt}
                  // label={'Frequency'}
                />
              )}
            />
          )
        }
      },
      { field: 'uom_name', title: translate('uom'), type: 'string', width: 100, editable: 'never' },
      {
        field: 'weight_percent',
        title: translate('weightPercent'),
        width: 125,
        render: rowData => {
          let value = rowData.weight_percent

          return (
            <TextField
              fullWidth
              value={isNaN(+value) ? '' : value}
              type={'number'}
              inputProps={{ min: 0, max: 100 }}
              error={value < 0 || value > 100}
              helperText={value < 0 || value > 100 ? translate('weightValidationHelperText') : null}
              onChange={e => inputChanged({ ...rowData, weight_percent: e.target.value })}
              onBlur={() =>
                inputChanged({ ...rowData, weight_percent: parseFloat(value || 0).toFixed(2) })
              }
            />
          )
        }
      },
      {
        field: 'is_deploy',
        title: translate('deployable'),
        render: rowData => {
          return (
            <Toggle
              inputValue={!!rowData.is_deploy}
              changed={(e, value) => {
                if (value && assignmentTargetIds.length > 1) {
                  fireAlert(translate('cannotDeployMultiTarget'))
                } else {
                  inputChanged({ ...rowData, is_deploy: value ? 1 : 0 })
                }
              }}
              disabled={!rowData.deployable_allowed || !!rowData.is_adopt}
            />
          )
        },
        width: 100
      },
      {
        field: 'is_adopt',
        title: translate('adoptable'),
        render: rowData => {
          return (
            <Toggle
              inputValue={!!rowData.is_adopt}
              changed={(e, value) => {
                inputChanged({ ...rowData, is_adopt: value ? 1 : 0 }, rowData.item_id, value)
              }}
              disabled={!rowData.adopt_allowed || !!rowData.is_deploy}
            />
          )
        },
        width: 100
      }
    ],
    STT02: [
      {
        field: 'item_code',
        title: translate('kpiCode'),
        editable: 'never',
        width: 100
      },
      {
        field: 'item_name',
        title: translate('kpiName'),
        editable: 'never',
        width: 250
      },
      {
        field: 'target_value',
        title: translate('expectation'),
        width: 75,
        render: rowData => {
          let isError = false
          let helperText = ''

          if (isThresholdScore) {
            const value =
              structureScoreOptions.find(
                item => +item.cs_id === +rowData.cs_id || +item.score === +rowData.target_value
              ) || null
            isError = value?.score > maxScore
            helperText = isError ? translate('scoreIsHigherThanTarget') : ''

            const changeHandler = (e, value) =>
              inputChanged({
                ...rowData,
                target_value: +value?.score || null,
                cs_id: +value?.cs_id || null
              })

            const getOptionLabel = option => option.score_name || ''

            return (
              <Autocomplete
                value={value}
                onChange={changeHandler}
                options={structureScoreOptions}
                getOptionLabel={getOptionLabel}
                renderInput={params => (
                  <TextField {...params} fullWidth error={isError} helperText={helperText} />
                )}
              />
            )
          } else {
            const value = isNaN(+rowData?.target_value) ? '' : +rowData?.target_value
            const inputProps = { max: maxScore, min: 0 }

            isError = value > maxScore
            helperText = isError ? translate('scoreIsHigherThanTarget') : ''

            const changeHandler = e => inputChanged({ ...rowData, target_value: e.target.value })
            const blurHandler = e =>
              inputChanged({ ...rowData, target_value: parseFloat(e.target.value).toFixed(2) })

            return (
              <TextField
                value={value}
                type={'number'}
                error={isError}
                fullWidth
                helperText={helperText}
                inputProps={inputProps}
                onChange={changeHandler}
                onBlur={blurHandler}
              />
            )
          }
        }
      },
      {
        field: 'weight_percent',
        title: translate('weightPercent'),
        width: 75,
        render: rowData => {
          let value = rowData.weight_percent
          return (
            <TextField
              value={isNaN(+value) ? '' : value}
              type={'number'}
              inputProps={{ min: 0, max: 100 }}
              fullWidth
              error={value < 0 || value > 100}
              helperText={value < 0 || value > 100 ? translate('weightValidationHelperText') : null}
              onChange={e => inputChanged({ ...rowData, weight_percent: e.target.value })}
              onBlur={e =>
                inputChanged({ ...rowData, weight_percent: parseFloat(e.target.value).toFixed(2) })
              }
            />
          )
        }
      }
    ],
    STT03: [
      {
        field: 'item_name',
        title: translate('kpiName'),
        editable: 'never',
        width: 250,
        render: rowData => {
          return (
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant={'body2'}>{rowData.item_name}</Typography>
              <StyledChip
                style={{ marginLeft: 5 }}
                icon={<Info />}
                variant={'outlined'}
                size={'small'}
                color={'primary'}
                onClick={() => openModalHandler(rowData)}
              />
            </span>
          )
        }
      },
      {
        field: 'max_value',
        title: translate('maxValue'),
        width: 100,
        render: rowData => {
          return (
            <TextField
              value={rowData.max_value}
              type={'number'}
              onChange={e => inputChanged({ ...rowData, max_value: e.target.value })}
              onBlur={() =>
                inputChanged({
                  ...rowData,
                  max_value: parseFloat(rowData.max_value || 0).toFixed(2)
                })
              }
            />
          )
        }
      },
      {
        field: 'unit_deduct_score',
        title: translate('deductScoreUnit'),
        width: 100,
        render: rowData => {
          return (
            <TextField
              value={rowData.unit_deduct_score}
              type={'number'}
              onChange={e => inputChanged({ ...rowData, unit_deduct_score: e.target.value })}
              onBlur={() =>
                inputChanged({
                  ...rowData,
                  unit_deduct_score: parseFloat(rowData.unit_deduct_score || 0).toFixed(2)
                })
              }
            />
          )
        }
      },
      {
        field: 'value_get_zero',
        title: translate('valueGetZero'),
        width: 100,
        render: rowData => {
          return (
            <TextField
              value={rowData.value_get_zero}
              type={'number'}
              onChange={e => inputChanged({ ...rowData, value_get_zero: e.target.value })}
              disabled={!isValueGetZero}
              onBlur={() =>
                inputChanged({
                  ...rowData,
                  value_get_zero: !isNaN(parseFloat(rowData.value_get_zero))
                    ? parseFloat(rowData.value_get_zero).toFixed(2)
                    : ''
                })
              }
            />
          )
        }
      }
    ],
    STT05: [
      {
        field: 'item_code',
        title: translate('kpiCode'),
        editable: 'never',
        width: 100
      },
      {
        field: 'item_name',
        title: translate('kpiName'),
        editable: 'never',
        width: 250,
        render: rowData => (
          <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant={'body2'}>{rowData.item_name}</Typography>
            <StyledChip
              style={{ marginLeft: 5 }}
              icon={<Info />}
              variant={'outlined'}
              size={'small'}
              color={'primary'}
              onClick={() => openModalHandler(rowData)}
            />
          </span>
        )
      },
      {
        field: 'target_value',
        title: translate('expectation'),
        width: 75,
        render: rowData => {
          const maxScore = rowData.item?.competency_max || rowData.competency_max || 0
          let value = isNaN(+rowData?.target_value) ? '' : +rowData?.target_value
          let isError = null
          let helperText = null

          if (value > maxScore) {
            isError = true
            helperText = translate('valueIsHigherThanCompetencyMax')
            value = ''
          } else if ((value * 10) % 2 !== 0) {
            isError = true
            helperText = translate('precisionIsNotEven')
            value = ''
          }

          return (
            <TextField
              // label={'Weight'}
              value={value}
              type={'number'}
              error={isError}
              fullWidth
              helperText={helperText}
              inputProps={{ max: maxScore, min: 0, step: 0.2 }}
              onChange={e => groupItemInputChanged({ ...rowData, target_value: e.target.value })}
              onBlur={e =>
                groupItemInputChanged({
                  ...rowData,
                  target_value: parseFloat(e.target.value).toFixed(2)
                })
              }
            />
          )
        }
      },
      {
        field: 'weight_percent',
        title: translate('weightPercent'),
        width: 75,
        render: rowData => displayNumber(rowData.weight_percent, 2)
      }
    ]
  }

  const rowActions = [
    {
      icon: () => <Add />,
      tooltip: translate('addItem'),
      onClick: () => addButtonClicked(),
      isFreeAction: true,
      disabled: actionDisabled
    },
    {
      icon: () => <Delete />,
      tooltip: translate('removeItem'),
      onClick: (e, rowData) => deleteItemClickedHandler(rowData),
      disabled: actionDisabled
    }
  ]

  const currentTableStructure = tableStructures[structureCode] || []

  const openModalHandler = dataRow => {
    setModalOpen(true)
    setTargetRow(dataRow)
  }

  const closeModalHandler = () => {
    setModalOpen(false)
    setTargetRow(null)
  }

  const deleteConfirmedHandler = useCallback(() => {
    if (structureCode === 'STT05') {
      groupItemDeleted(targetRow)
    } else {
      deleteRowClicked(targetRow)
    }
    setTargetRow(null)
    setConfirmDialogOpen(false)
  }, [
    targetRow,
    structureCode,
    groupItemDeleted,
    deleteRowClicked,
    setTargetRow,
    setConfirmDialogOpen
  ])

  const deleteCancelledHandler = useCallback(() => {
    setTargetRow(null)
    setConfirmDialogOpen(false)
  }, [setTargetRow, setConfirmDialogOpen])

  let detailPanel = null
  let tableFooter = null
  if (structureCode === 'STT01') {
    const targetScore = structureData.target_score
    // const a = responsibleOptions
    detailPanel = rowData => {
      return (
        <DetailPanel
          appraisalType={appraisalType}
          targetScore={targetScore}
          rowData={rowData}
          frequencyOptions={frequencyOptions}
          responsibleSearchChanged={responsibleSearchChanged}
          // responsibleOptions={responsibleOptions.responsible_list[item]}
          applyButtonClicked={() => applyButtonClicked(rowData)}
          inputChanged={updatedData => inputChanged({ ...rowData, ...updatedData })}
          loadingQueue={loadingQueue}
        />
      )
    }
  }

  if (['STT01', 'STT02', 'STT05'].includes(structureCode)) {
    let sumWeight = 0
    if (structureCode !== 'STT05') {
      sumWeight = parseFloat(_.sumBy(structureData?.result_item, item => +item.weight_percent || 0))
    } else {
      sumWeight = parseFloat(
        Object.values(structureData.result_item).reduce(
          (sum, group) => sum + +group.weight_percent || 0,
          0
        )
      )
    }
    const error = !isTrulyEqual(+sumWeight, 100)
    tableFooter = (
      <Grid container justify={'flex-end'} alignItems={'center'} style={{ marginTop: '1rem' }}>
        <Grid item>
          <TextField
            label={translate('totalPercentWeight')}
            value={parseFloat(sumWeight || 0).toFixed(2)}
            variant={'outlined'}
            size={'small'}
            inputProps={{ readOnly: true }}
            error={error}
            helperText={!error ? null : translate('totalWeightValidationHelperText')}
          />
        </Grid>
      </Grid>
    )

    if (!structureGetZero) {
      currentTableStructure.map(item => ({ ...item, render: () => null }))
    }
  }

  let tableDisplay = null

  if (['STT05'].includes(structureCode)) {
    const competencyGroup = Object.entries(structureData.result_item || {}).map(com => {
      const [comCode, comAttrs] = com
      const items = comAttrs.items
      const itemLength = items.length || 1
      return {
        code: comCode,
        name: comAttrs.competency_type_name,
        weight_percent: comAttrs.weight_percent || 0,
        items: items.map(item => ({
          ...item,
          weight_percent: (comAttrs.weight_percent || 0) / itemLength
        }))
      }
    })
    tableDisplay = competencyGroup.map(group => (
      <Collapse
        show={collapseOpen.includes(group.code)}
        toggleBarClicked={e => collapseBarClickedHandler(group.code, { ...e })}
        key={group.code}
        titleComponent={
          <Grid container justify={'space-between'} alignItems={'center'}>
            <Grid item xs sm md lg>
              <Typography align={'left'}>{group.name}</Typography>
            </Grid>
            <Grid item xs={4} sm={3} md={2} lg={1}>
              <InputGroup
                label={'weightPercent'}
                inputType={'input'}
                inputValue={group.weight_percent}
                noMargin
                configs={{
                  type: 'number',
                  min: 0,
                  max: 100,
                  size: 'small',
                  onBlur: e => {
                    groupInputChanged({
                      ...structureData,
                      result_item: {
                        ...structureData.result_item,
                        [group.code]: {
                          ...structureData.result_item[group.code],
                          weight_percent: getPureNumber(e.target.value, 2)
                        }
                      }
                    })
                  },
                  onChange: e =>
                    groupInputChanged({
                      ...structureData,
                      result_item: {
                        ...structureData.result_item,
                        [group.code]: {
                          ...structureData.result_item[group.code],
                          weight_percent: e.target.value
                        }
                      }
                    })
                }}
              />
            </Grid>
          </Grid>
        }>
        <Grid container>
          <Grid item xs>
            <MTable
              structure={currentTableStructure}
              rowActions={rowActions}
              detailPanel={detailPanel}
              recordData={group.items || []}
              search={false}
              paginationDisabled
              sortingDisabled
              localization={{
                header: {
                  actions: translate('action')
                }
              }}
            />
          </Grid>
        </Grid>
      </Collapse>
    ))
  } else {
    tableDisplay = (
      <MTable
        title={`${translate('structureWeight')}: ${parseFloat(
          structureData.weight_percent || 0
        ).toFixed(2)} %`}
        structure={currentTableStructure}
        rowActions={rowActions}
        detailPanel={detailPanel}
        recordData={structureData?.result_item || []}
        search={false}
        paginationDisabled
        sortingDisabled
        localization={{
          header: {
            actions: translate('action')
          }
        }}
      />
    )
  }

  return (
    <Aux>
      <ConfirmationModal
        open={confirmDialogOpen}
        confirmed={deleteConfirmedHandler}
        cancelled={deleteCancelledHandler}
        promptText={translate('confirmDel')}
      />
      <PerformanceLevelModal
        modalOpen={modalOpen}
        closeModalHandler={closeModalHandler}
        translate={translate}
        targetRow={targetRow}
        setTargetRow={setTargetRow}
        inputChanged={structureCode === 'STT05' ? groupInputChanged : inputChanged}
      />
      {tableDisplay}
      {tableFooter}
    </Aux>
  )
}

const StyledChip = withStyles(theme => ({
  labelSmall: {
    paddingLeft: 6,
    paddingRight: 0
  }
}))(Chip)

export default withTranslate(StructureTable)
